import React, { useState, useEffect } from 'react'
import { CircularProgress } from '@material-ui/core'
import { Text } from '@tremor/react'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { formatDashboardNumber } from '../../utils/formatDashboardNumber'
import { LevelChangeSummaryData, LevelChangeSummaryParams, getLevelChangeSummary } from './api'

interface LevelChangeSummaryProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const LevelChangeSummary: React.FC<LevelChangeSummaryProps> = ({
  period,
  storeId = undefined,
  trigger,
  onLoaded,
}) => {
  const [data, setData] = useState<LevelChangeSummaryData | null>(null)
  const [statsCall, statsLoading] = useApiCall<
    LevelChangeSummaryParams,
    { data: LevelChangeSummaryData }
  >(getLevelChangeSummary)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'LevelChangeSummary' })

  const fetchData = async () => {
    try {
      const response = await statsCall({
        startDate: moment()
          .year(period.year)
          .month(period.month)
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: moment()
          .year(period.year)
          .month(period.month)
          .endOf('month')
          .format('YYYY-MM-DD'),
        storeId,
      })
      if (response?.data) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error fetching level change summary:', error)
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  if (statsLoading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-8">
        <div className="bg-white rounded-2xl shadow p-6 flex flex-col h-full">
          <span
            className="text-base font-bold mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
          >
            Usuarios que subieron de nivel
          </span>
          <div className="flex items-center justify-center h-20">
            {statsLoading ? (
              <CircularProgress size={40} />
            ) : (
              <Text>No hay datos disponibles para mostrar en este momento.</Text>
            )}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow p-6 flex flex-col h-full">
          <span
            className="text-base font-bold mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
          >
            Usuarios que bajaron de nivel
          </span>
          <div className="flex items-center justify-center h-20">
            {statsLoading ? (
              <CircularProgress size={40} />
            ) : (
              <Text>No hay datos disponibles para mostrar en este momento.</Text>
            )}
          </div>
        </div>
      </div>
    )
  }

  const cardsData = [
    {
      title: 'Usuarios que subieron de nivel',
      metrics: [
        { label: 'Cantidad de clientes', value: data.upgraded.count },
        {
          label: 'Share sobre clientes activos del período',
          value: `${data.upgraded.percentage}%`,
        },
      ],
    },
    {
      title: 'Usuarios que bajaron de nivel',
      metrics: [
        { label: 'Cantidad de clientes', value: data.downgraded.count },
        {
          label: 'Share sobre clientes activos del período',
          value: `${data.downgraded.percentage}%`,
        },
      ],
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-8">
      {cardsData.map(card => (
        <div key={card.title} className="bg-white rounded-2xl shadow p-6 flex flex-col h-full">
          <span
            className="text-base font-bold mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
          >
            {card.title}
          </span>
          <div className="flex flex-row gap-12">
            {card.metrics.map(metric => (
              <div key={metric.label} className="flex flex-col items-start">
                <span className="text-sm font-semibold mb-1 text-gray-700">{metric.label}</span>
                <span
                  className="text-3xl font-extrabold"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }}
                >
                  {typeof metric.value === 'number'
                    ? formatDashboardNumber(metric.value)
                    : metric.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default LevelChangeSummary
